import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/Kpi";
import { dateIt, eurShort, num } from "@/lib/format";
import DealerForm from "@/components/DealerForm";
import { useAuth } from "@/context/AuthContext";

export const ScorporoDealer = ({ meta, onDone }) => {
  const { user } = useAuth();
  const soloMaster = ["MASTER", "ADMIN"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [tick, setTick] = useState(0);
  const [assoc, setAssoc] = useState(null);      // riga per cui si sta scegliendo il convenzionato esistente
  const [cerca, setCerca] = useState("");
  const [trovati, setTrovati] = useState([]);

  useEffect(() => {
    if (!assoc || cerca.trim().length < 2) { setTrovati([]); return; }
    const t = setTimeout(() => api.get(`/dealers?search=${encodeURIComponent(cerca.trim())}&page_size=8`)
      .then((r) => setTrovati(r.data.rows || [])).catch(() => setTrovati([])), 250);
    return () => clearTimeout(t);
  }, [cerca, assoc]);

  const associa = async (dest) => {
    if (!window.confirm(`Associare il codice ${assoc.codice_dealer} (${assoc.dealer_nome || "—"}) a `
      + `${dest.ragione_sociale} (${dest.codice_dealer})?\n\nLe ${assoc.pratiche} pratiche passano sotto questo `
      + "convenzionato e le produzioni vengono unificate, anche per gli import futuri.")) return;
    try {
      const r = await api.post(`/dealers/associa?codice_originale=${encodeURIComponent(assoc.codice_dealer)}&codice_destinazione=${encodeURIComponent(dest.codice_dealer)}`);
      toast.success(`${num(r.data.pratiche_spostate)} pratiche unificate in ${r.data.destinazione}`);
      setAssoc(null); setCerca(""); setSel(null);
      setTick((t) => t + 1);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    if (!soloMaster) return;
    api.get("/dealers/non-identificato/codici").then((r) => setRows(r.data.rows)).catch(() => setRows([]));
  }, [onDone, tick, soloMaster]);

  const escludi = async (r) => {
    if (!window.confirm(`Il codice ${r.codice_dealer}${r.dealer_nome ? ` (${r.dealer_nome})` : ""} non è un `
      + "convenzionato del canale lungo?\n\nLe sue pratiche verranno eliminate e il codice sarà "
      + "scartato in tutti gli import futuri.")) return;
    try {
      await api.post(`/dealers/escludi?codice=${encodeURIComponent(r.codice_dealer)}`);
      toast.success(`Codice ${r.codice_dealer} escluso dal canale lungo`);
      setSel(null);
      setTick((t) => t + 1);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  const salva = async (dati) => {
    try {
      const r = await api.post(`/dealers/scorpora?codice_originale=${sel.codice_dealer}`, dati);
      toast.success(`Convenzionato creato · ${num(r.data.pratiche_spostate)} pratiche scorporate`);
      setSel(null);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!soloMaster || !rows.length) return null;

  return (
    <Card className="p-6 mb-6 !border-2 !border-amber-400" data-testid="scorporo-panel">
      <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-amber-700 mb-1">
        Codici da identificare · {num(rows.length)}
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Crea l'anagrafica del convenzionato per attribuire le sue pratiche, oppure segnala che il codice
        non è un convenzionato (es. un'agenzia): in quel caso sparisce per sempre dal canale lungo.
        Ogni codice si gestisce in modo indipendente.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Codice</th>
              <th className="text-left px-3 py-2">Nome nel file</th>
              <th className="text-right px-3 py-2">Pratiche</th>
              <th className="text-right px-3 py-2">Importo</th>
              <th className="text-left px-3 py-2">Settore</th>
              <th className="text-left px-3 py-2">Periodo</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.codice_dealer} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono">{r.codice_dealer}</td>
                <td className="px-3 py-2 text-slate-600">{r.dealer_nome || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{num(r.pratiche)}</td>
                <td className="px-3 py-2 text-right font-mono">{eurShort(r.importo)}</td>
                <td className="px-3 py-2 text-slate-500">{r.settore}</td>
                <td className="px-3 py-2 text-slate-500">
                  {dateIt(r.prima_pratica)}{r.ultima_pratica !== r.prima_pratica ? ` → ${dateIt(r.ultima_pratica)}` : ""}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button type="button" data-testid={`btn-scorpora-${r.codice_dealer}`}
                    onClick={() => { setAssoc(null); setSel(sel?.codice_dealer === r.codice_dealer ? null : r); }}
                    className="px-3 py-1.5 rounded-md bg-amber-600 text-white text-xs hover:bg-amber-700 transition-colors duration-200">
                    {sel?.codice_dealer === r.codice_dealer ? "Annulla" : "Crea anagrafica"}
                  </button>
                  <button type="button" data-testid={`btn-associa-${r.codice_dealer}`}
                    onClick={() => { setSel(null); setAssoc(assoc?.codice_dealer === r.codice_dealer ? null : r); setCerca(""); }}
                    className="ml-2 px-3 py-1.5 rounded-md border border-[#0018A8] text-[#0018A8] text-xs hover:bg-blue-50 transition-colors duration-200">
                    {assoc?.codice_dealer === r.codice_dealer ? "Annulla" : "Associa a esistente"}
                  </button>
                  <button type="button" data-testid={`btn-escludi-${r.codice_dealer}`}
                    onClick={() => escludi(r)}
                    className="ml-2 px-3 py-1.5 rounded-md border border-red-300 text-red-700 text-xs hover:bg-red-50 transition-colors duration-200">
                    Non è un convenzionato
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {assoc && (
        <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="associa-form">
          <div className="text-sm text-slate-700 mb-2">
            Codice <b className="font-mono">{assoc.codice_dealer}</b> {assoc.dealer_nome ? `· ${assoc.dealer_nome}` : ""} → cerca il convenzionato esistente a cui unificare la produzione
          </div>
          <input autoFocus value={cerca} onChange={(e) => setCerca(e.target.value)} data-testid="associa-cerca"
            placeholder="Ragione sociale o codice convenzionato…"
            className="w-full sm:w-96 px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:border-[#0018A8]" />
          {trovati.length > 0 && (
            <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200 bg-white sm:w-[640px]">
              {trovati.map((d) => (
                <li key={d.codice_dealer} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span><b className="font-mono text-slate-500 mr-2">{d.codice_dealer}</b>{d.ragione_sociale}
                    <span className="text-slate-400 text-xs ml-2">{d.settore || ""}{d.commerciale_nome ? ` · ${d.commerciale_nome}` : ""}</span></span>
                  <button type="button" onClick={() => associa(d)} data-testid={`associa-a-${d.codice_dealer}`}
                    className="px-3 py-1 rounded-md bg-[#0018A8] text-white text-xs hover:bg-[#001580]">Unifica qui</button>
                </li>
              ))}
            </ul>
          )}
          {cerca.trim().length >= 2 && !trovati.length && <div className="mt-2 text-xs text-slate-400">Nessun convenzionato trovato.</div>}
        </div>
      )}

      {sel && (
        <div className="mt-6" data-testid="scorporo-form">
          <DealerForm meta={meta} onCancel={() => setSel(null)} onSubmitOverride={salva}
            preset={{
              codice_dealer: sel.codice_dealer, ragione_sociale: "",
              commerciale_slug: sel.commerciale_slug === "altro" ? "" : sel.commerciale_slug || "",
              settore: sel.settore || "", data_attivazione: sel.prima_pratica || "",
            }} />
        </div>
      )}
    </Card>
  );
};
